import React, { useState, useEffect, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import { CancelRequest } from "../../../../../services/AxiosInstance";
import { useNavigate } from "react-router-dom";
import Pagination from "../../../../constant/Pagination";
import TableLoaderEffect from "../../../../constant/TableLoader";
import DebounceSearchInput from "../../../../constant/DebounceSearchInput";
import { getDocumenrVersionApi } from "../../../../../services/DocumentService";
import VersionStatus from "./VersionStatus";
import { connect } from "react-redux";
import ViewVersionContentModal from "./ViewVersionContentModal";

const DocumentVersion = ({ userPermission, user }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [query, setSearchQuery] = useState("");
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const recordsPage = 25;
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const updateActiveKey = location.state?.activekey || "document";

  const fetchData = useCallback(() => {
    CancelRequest();
    setIsLoading(true);
    getDocumenrVersionApi(id, currentPage, recordsPage, query)
      .then((resp) => {
        setData(resp.data.results);
        setTotal(resp.data.total);
      })
      .catch((error) => {
        console.error(error.response?.data?.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [currentPage, query]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePagination = (page) => {
    setCurrentPage(page.selected + 1);
  };

  const handleAfterUpdate = (updatedData) => {
    setData((prevData) =>
      prevData.map((item) =>
        item.id === updatedData.id ? { ...item, ...updatedData } : item
      )
    );
  };

  const handleSearchChange = (value) => {
    CancelRequest();
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const isAddButtonDisabled = data.some((item) => item.status === "issued");

  const hasEditableActions = data.some(
    (item) => item.created_by?.id === user?.id || item?.status !== "issued"
  );

  return (
    <div className="row">
      <div className="col-xl-12">
        <div className="card">
          <div className="card-body p-0">
            <div className="table-header">
              <div className="d-flex align-items-center">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm btn-circle"
                  onClick={() => navigate("/document")}
                >
                  <i className="fa-solid fa-arrow-left"></i>
                </button>
                <h4 className="mb-0 ms-2">Document Versions</h4>
              </div>
              <div className="table-left-header">
                <div className="form-group me-3">
                  <DebounceSearchInput
                    handleSearchChange={handleSearchChange}
                  />
                </div>

                {isAddButtonDisabled && userPermission.create && (
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      navigate(`/add-version/${id}`, {
                        state: { updateActiveKey },
                      });
                    }}
                  >
                    Add Version
                  </button>
                )}
              </div>
            </div>

            <div className="table-responsive active-projects style-1 ItemsCheckboxSec shorting">
              <table
                id="empoloyees-tblwrapper"
                className="table ItemsCheckboxSec dataTable no-footer mb-0"
              >
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Description</th>
                    <th>Created By</th>
                    <th>Reviewed By</th>
                    <th>Approved By</th>
                    <th>Issued By</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <TableLoaderEffect colLength={hasEditableActions ? 8 : 7} />
                  ) : (
                    data.map((item, index) => (
                      <tr key={index}>
                        <td>{item?.version_number}</td>
                        <td><span className="text-wrap">{item?.version_description}</span></td>
                        <td>
                          <div className="d-flex flex-column">
                            <strong>{item.created_by?.name}</strong>
                            <span>{item.created_by?.created_at}</span>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column">
                            <strong>{item.reviewed_by?.name}</strong>
                            <span>{item.reviewed_by?.reviewed_at}</span>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column">
                            <strong>{item.approved_by?.name}</strong>
                            <span>{item.approved_by?.approved_at}</span>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column">
                            <strong>{item.issued_by?.name}</strong>
                            <span>{item.issued_by?.issued_at}</span>
                          </div>
                        </td>
                        <td>
                          <VersionStatus
                            item={item}
                            handleAfterUpdate={handleAfterUpdate}
                          />
                        </td>
                          <td>
                            {item.file ? (
                              <a
                                href={`${process.env.REACT_APP_BASE_URL}${item.file.file_path}`}
                                className="text-info"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <i className="fa-solid fa-eye"></i>
                              </a>
                            ) : (
                              <ViewVersionContentModal
                                htmlContent={item.content}
                              />
                            )}
                            {item.created_by?.id === user?.id && hasEditableActions &&
                              ["pending", "rejected"].includes(
                                item?.status
                              ) && (
                                <span
                                  className="c-pointer"
                                  onClick={() => {
                                    navigate(`/edit-document-version/${id}`, {
                                      state: { data: item, updateActiveKey },
                                    });
                                  }}
                                >
                                  <i className="fa-solid fa-pen-to-square text-primary"></i>
                                </span>
                              )}
                          </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {isLoading ? (
              <div style={{ height: "4rem" }}></div>
            ) : (
              <Pagination
                currentPage={currentPage}
                recordsPage={recordsPage}
                dataLength={total}
                handlePagination={handlePagination}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  userPermission: state.auth.user?.document_permission,
  user: state.auth.user,
});
export default connect(mapStateToProps)(DocumentVersion);
